import { index, pgTable, primaryKey, text } from 'drizzle-orm/pg-core'

import { user } from './auth.ts'
import { booking } from './bookings.ts'
import { createdAt } from './columns.ts'

/**
 * Who from the company sits in a meeting — every one of them.
 *
 * Owner: „jedno spotkanie na godzinę, nawet jeśli kilka osób wolnych — wtedy
 * wszystkie osoby wolne uczestniczą w jednym spotkaniu". So a strategy hour or
 * a free call has a SET of people on it, and `booking.performer_user_id` is
 * one column: overloading it would have meant picking one host and hiding the
 * rest, which is the opposite of what was asked.
 *
 * ── WHAT A ROW MEANS ─────────────────────────────────────────────────────
 * "This person is in this meeting, so the hour is theirs." The timeline
 * readers treat a row here exactly as they treat `performer_user_id`: the
 * booking is busy time for that person, on every conflict check and in their
 * own calendar. A meeting written before this table existed has no rows and
 * stays as it was; nothing is backfilled.
 *
 * Written by the meeting paths only (free call, strategy checkout, panel
 * create, reschedule), each inside the transaction that claims the hour, under
 * the offering's advisory lock — see `freeMeetingHosts`.
 *
 * `CASCADE` both ways. The booking going takes its attendance with it, and so
 * does an account that is truly deleted — a row naming nobody answers nothing.
 * A CLOSED account is anonymised rather than deleted, so its rows stay, and
 * that is correct: they name no personal data, only that an account (now a
 * tombstone) sat in a meeting. Kept on record, never read as present:
 * `listBookingHostsFor` skips banned accounts, closed ones included.
 */
export const bookingHost = pgTable(
  'booking_host',
  {
    bookingId: text('booking_id')
      .notNull()
      .references(() => booking.id, { onDelete: 'cascade' }),
    userId: text('user_id')
      .notNull()
      .references(() => user.id, { onDelete: 'cascade' }),

    createdAt: createdAt(),
  },
  (t) => [
    primaryKey({ columns: [t.bookingId, t.userId] }),
    // "Which meetings is this person in?" — their calendar, and the busy test.
    index('booking_host_user_idx').on(t.userId),
  ],
)

export type BookingHost = typeof bookingHost.$inferSelect
export type NewBookingHost = typeof bookingHost.$inferInsert
